/**
 * Shared bulk selection hook and utilities for unassigned meeting notes.
 * Used by Granola, Fireflies, and Otter unassigned note components.
 */

import { useState, useCallback, useMemo } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '@/lib/firebase';
import { toast } from 'sonner';

// ============================================================================
// SELECTION HOOK
// ============================================================================

export function useBulkSelection<T extends { id: string }>(items: T[]) {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const toggle = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const selectAll = useCallback(() => {
    setSelectedIds(new Set(items.map((item) => item.id)));
  }, [items]);

  const clearAll = useCallback(() => {
    setSelectedIds(new Set());
  }, []);

  const toggleAll = useCallback(() => {
    if (selectedIds.size === items.length) {
      clearAll();
    } else {
      selectAll();
    }
  }, [selectedIds.size, items.length, selectAll, clearAll]);

  const isAllSelected = useMemo(
    () => items.length > 0 && selectedIds.size === items.length,
    [items.length, selectedIds.size]
  );

  const isSomeSelected = useMemo(
    () => selectedIds.size > 0 && selectedIds.size < items.length,
    [selectedIds.size, items.length]
  );

  // Clean up selections when items change (e.g., after bulk action)
  const validSelectedIds = useMemo(() => {
    const itemIds = new Set(items.map((i) => i.id));
    const valid = new Set<string>();
    for (const id of selectedIds) {
      if (itemIds.has(id)) valid.add(id);
    }
    return valid;
  }, [items, selectedIds]);

  return {
    selectedIds: validSelectedIds,
    selectedCount: validSelectedIds.size,
    toggle,
    selectAll,
    clearAll,
    toggleAll,
    isAllSelected,
    isSomeSelected,
    isSelected: (id: string) => validSelectedIds.has(id),
  };
}

// ============================================================================
// BULK ACTIONS
// ============================================================================

interface BulkActionResult {
  success: boolean;
  count: number;
  message: string;
}

export async function bulkAssignNotes(
  documentIds: string[],
  projectId: string
): Promise<BulkActionResult | null> {
  try {
    const fn = httpsCallable<
      { action: string; documentIds: string[]; projectId: string },
      BulkActionResult
    >(functions, 'bulkManageUnassignedNotes');

    const result = await fn({ action: 'assign', documentIds, projectId });
    const data = result.data;

    if (data.count > 0) {
      toast.success(`${data.count} note${data.count > 1 ? 's' : ''} assigned to project`);
    } else {
      toast.info('No notes were assigned (they may have already been assigned)');
    }

    return data;
  } catch (error) {
    const msg = error instanceof Error ? error.message : 'Failed to assign notes';
    toast.error(msg);
    return null;
  }
}

export async function bulkDeleteNotes(
  documentIds: string[]
): Promise<BulkActionResult | null> {
  try {
    const fn = httpsCallable<
      { action: string; documentIds: string[] },
      BulkActionResult
    >(functions, 'bulkManageUnassignedNotes');

    const result = await fn({ action: 'delete', documentIds });
    const data = result.data;

    if (data.count > 0) {
      toast.success(`${data.count} note${data.count > 1 ? 's' : ''} deleted`);
    }

    return data;
  } catch (error) {
    const msg = error instanceof Error ? error.message : 'Failed to delete notes';
    toast.error(msg);
    return null;
  }
}
